import { onBeforeUnmount, onMounted } from "vue";

const APP_HEIGHT = "--app-height";
const KEYBOARD_INSET = "--keyboard-inset";
const KEYBOARD_OPEN_CLASS = "--keyboard-open";

/** Ниже этого значения "сжатие" вьюпорта считаем не клавиатурой, а панелью браузера. */
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * Держит в CSS-переменных на <html> реальную высоту видимой области:
 *
 *  --app-height     — высота visual viewport (то, что реально видит пользователь,
 *                     без адресной строки и без клавиатуры);
 *  --keyboard-inset — сколько снизу занимает экранная клавиатура.
 *
 * Зачем: 100vh на iOS считается без учёта адресной строки и не реагирует на
 * клавиатуру, из-за чего кнопки отправки уезжают за пределы экрана. Android это
 * решает через interactive-widget=resizes-content в meta viewport, iOS — нет.
 *
 * Вызывается один раз в App.vue.
 */
export function useAppViewport(): void {
  let frame = 0;

  const apply = () => {
    frame = 0;
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const height = viewport?.height ?? window.innerHeight;

    // На iOS layout viewport не сжимается — разница и есть клавиатура.
    // На Android с resizes-content window.innerHeight уже сжат, разница ≈ 0.
    const inset = viewport
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;

    root.style.setProperty(APP_HEIGHT, `${Math.round(height)}px`);
    root.style.setProperty(KEYBOARD_INSET, `${Math.round(inset)}px`);
    root.classList.toggle(KEYBOARD_OPEN_CLASS, inset > KEYBOARD_MIN_HEIGHT);
  };

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(apply);
  };

  onMounted(() => {
    apply();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    onBeforeUnmount(() => {
      if (frame) window.cancelAnimationFrame(frame);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
    });
  });
}
